import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { Controller, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import { NavLink, useParams } from 'react-router-dom';

import Messages from '../defs/messages';
import { usePostResetPasswordMutation } from '../store/apiSlice';
import { handlePageChange } from '../utils/ally';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
	},
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
	},
}));

function getResetErrorMessage(err) {
	if (typeof err?.data?.errors?.[0]?.msg === 'string') {
		return err.data.errors[0].msg;
	}

	if (typeof err?.data?.message === 'string' && err.data.message.trim()) {
		return err.data.message;
	}

	return Messages.RESET_PASSWORD.INVALID_TOKEN;
}

function ResetPassword() {
	const classes = useStyles();
	const headingRef = useRef(null);
	const { token = '' } = useParams();
	const msg = Messages.RESET_PASSWORD;
	const [resetPassword, { isLoading }] = usePostResetPasswordMutation();
	const [serverMessage, setServerMessage] = useState('');
	const [serverError, setServerError] = useState('');

	const {
		handleSubmit,
		control,
		reset,
		watch,
		formState: { errors },
	} = useForm({
		defaultValues: {
			password: '',
			password2: '',
		},
	});

	useEffect(() => {
		handlePageChange(msg.HEADER_MESSAGE, headingRef);
	}, [msg.HEADER_MESSAGE]);

	const onSubmit = async (values) => {
		setServerMessage('');
		setServerError('');

		if (!token) {
			setServerError(msg.INVALID_TOKEN);
			return;
		}

		try {
			const response = await resetPassword({
				token,
				password: values.password,
				password2: values.password2,
			}).unwrap();
			setServerMessage(response?.message || Messages.PASSWORD_RESET_SUCCESS);
			reset();
		} catch (err) {
			setServerError(getResetErrorMessage(err));
		}
	};

	return (
		<div className={classes.root}>
			<CssBaseline />
			<Helmet>
				<title>{msg.HEADER_MESSAGE}</title>
			</Helmet>
			<Container maxWidth="sm" className={classes.container}>
				<Paper className={classes.paper}>
					<Typography ref={headingRef} component="h1" variant="h4" role="alert">
						{msg.HEADER_MESSAGE}
					</Typography>
					<Typography component="p">{msg.DESCRIPTION}</Typography>

					{!token ? (
						<Typography color="primary" role="alert">
							{msg.INVALID_TOKEN}
						</Typography>
					) : (
						<form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
							<Controller
								name="password"
								control={control}
								rules={{
									required: {
										value: true,
										message: Messages.ERROR_PASSWORD_EMPTY,
									},
									minLength: {
										value: 8,
										message: Messages.ERROR_MIN_PASSWORD,
									},
									pattern: {
										value: /^(?=.*\d)(?=.*[~!@#$%^&*()_+`]).{8,}$/,
										message: Messages.ERROR_PASSWORD_CHARS,
									},
								}}
								render={({ field }) => (
									<TextField
										label="Нова парола"
										type="password"
										error={!!errors.password}
										helperText={errors?.password?.message}
										autoComplete="new-password"
										{...field}
									/>
								)}
							/>

							<Controller
								name="password2"
								control={control}
								rules={{
									required: {
										value: true,
										message: Messages.ERROR_REPEAT_PASSWORD_EMPTY,
									},
									validate: (value) =>
										value === watch('password') ||
										Messages.ERROR_PASSWORD_MISMATCH,
								}}
								render={({ field }) => (
									<TextField
										label="Повторете новата парола"
										type="password"
										error={!!errors.password2}
										helperText={errors?.password2?.message}
										autoComplete="new-password"
										{...field}
									/>
								)}
							/>

							{serverError ? (
								<Typography color="primary" role="alert">
									{serverError}
								</Typography>
							) : null}

							{serverMessage ? (
								<Typography role="status" aria-live="polite">
									{serverMessage}
								</Typography>
							) : null}

							<Grid container spacing={2}>
								<Grid item xs={12} sm="auto">
									<Button
										type="submit"
										color="primary"
										variant="outlined"
										disabled={isLoading}
									>
										{msg.SUBMIT_BUTTON}
									</Button>
								</Grid>
								<Grid item xs={12} sm="auto">
									<Button component={NavLink} to="/sign-in" color="primary">
										{msg.BACK_TO_LOGIN}
									</Button>
								</Grid>
							</Grid>
						</form>
					)}
				</Paper>
			</Container>
		</div>
	);
}

export default ResetPassword;
